import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { PropertyDto } from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { ApplicationStore } from '@workspace/ng-data-access';
import { filter, tap } from 'rxjs';
import { PropertyStatusBadgeComponent } from '../../../property-status-badge';
import { PropertyMembersDialogComponent } from '../property-members-dialog/property-members-dialog.component';
import { PropertyPdfExportDialogComponent } from '../property-pdf-export-dialog/property-pdf-export-dialog.component';

@Component({
  selector: 'etxe-property-view-sidebar',
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    PropertyStatusBadgeComponent,
  ],
  templateUrl: './property-view-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'col-span-1 lg:col-span-4 flex flex-col gap-6 lg:sticky lg:top-6',
  },
})
export class PropertyViewSidebarComponent {
  protected readonly appStore = inject(ApplicationStore);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertSnackbar = inject(AlertSnackBarService);

  property = input.required<PropertyDto>();
  isOwner = input<boolean>(false);
  isCoManager = input<boolean>(false);
  missingItemsCount = input<number>(0);
  demoMode = input<boolean>(false);

  readonly canManage = computed(() => this.isOwner() || this.isCoManager());

  shareClicked = output<void>();
  previewAgentClicked = output<void>();
  propertyUpdated = output<PropertyDto>();

  onDemoEditAction(label: string): void {
    this.alertSnackbar.info({
      title: 'Mode démonstration',
      message: `La section « ${label} » est préconfigurée dans cette étude de cas interactive.`,
    });
  }

  openMembersDialog(): void {
    if (this.demoMode()) {
      this.onDemoEditAction('Co-gestionnaires et invitations');
      return;
    }

    const dialogRef = this.dialog.open(PropertyMembersDialogComponent, {
      data: {
        property: this.property(),
        isOwner: this.isOwner(),
      },
      width: '600px',
      maxWidth: '95vw',
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is PropertyDto => Boolean(result)),
        tap((updated) => this.propertyUpdated.emit(updated)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  openPdfExportDialog(): void {
    this.dialog.open(PropertyPdfExportDialogComponent, {
      data: {
        property: this.property(),
        isOwner: this.isOwner(),
      },
      width: '980px',
      maxWidth: '96vw',
      height: '92vh',
      maxHeight: '92vh',
      panelClass: 'etxe-pdf-export-dialog-panel',
      autoFocus: false,
    });
  }
}
